import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';

import * as Edge from '../interfaces/edge';
import * as Node from '../interfaces/node';

import {Question} from 'shared/models/questions/question';
import {QuestionChain} from 'shared/models/questions/question-chain';
import {QuestionChainIds} from 'shared/models/questions/question-chain-ids';
import {Tiplist} from 'shared/models/tips/tiplist';
import {QuestionTypeFactoryService} from 'shared/questions/services/question-type-factory.service';
import {TreeViewRepository} from './../repositories/tree-view.repository';
import { Jump } from '../interfaces/jump';

interface ChainPathElement {
  question: string;
  answer: string;
}

type ChainPath = Array<ChainPathElement>;

@Injectable()
export class TreeViewService {

  constructor(private treeViewRepository: TreeViewRepository,
              private questionTypeFactoryService: QuestionTypeFactoryService) { }

  public getAllQuestions(): Observable<Array<Question>> {
    return this.treeViewRepository.getAllQuestions();
  }

  public getAllTipLists(): Observable<Array<Tiplist>> {
    return this.treeViewRepository.getAllTipLists();
  }

  public loadTree(treeId: string): Observable<Array<QuestionChain>> {
    return this.treeViewRepository.loadTree(treeId)
      .map((tree: Array<QuestionChain>) => {
        return tree.map((chain: QuestionChain) => {
          return this.mapQuestionTypesToClass(chain);
        });
      });
  }

  public saveTree(nodes: Node.Set, edges: Edge.Set, rootNode: Node.Node): Observable<Array<QuestionChain>> {
    return this.treeViewRepository.updateTree(this.getTree(rootNode, nodes, edges));
  }

  public getTree(rootNode: Node.Node, allNodes: Node.Set, allEdges: Edge.Set, currentPath: ChainPath = []): Array<QuestionChainIds> {
    if (rootNode.data && Node.Tools.isJump(rootNode.data)) {
      let jump: Array<string> = [];
      if (rootNode.data) {
        jump = this.getJumpAnswers(rootNode.data._id, allNodes, allEdges);
      }
      return [this.questionChainFromPath(currentPath.concat({
        question: (<Jump> (rootNode.data)).question._id + '',
        answer: '',
      }), null, jump)];
    }

    if (!rootNode.data || !Node.Tools.isQuestion(rootNode.data)) {
      let tiplistId: string;
      if (rootNode.data) {
        tiplistId = rootNode.data._id;
      }
      return [this.questionChainFromPath(currentPath, tiplistId, null)];
    }

    const edgesAtRoot: Array<Edge.Edge> = this.getEdgesForNode(rootNode, allEdges);
    const subchains: Array<Array<QuestionChainIds>> = edgesAtRoot.map((edge: Edge.Edge) => {
      const path: ChainPath = currentPath.concat({
        question: rootNode.data._id,
        answer: edge.answerValue,
      });
      return this.getTree(allNodes.get(edge.to), allNodes, allEdges, path);
    });

    return [].concat.apply([], subchains);
  }

  private questionChainFromPath(paths: ChainPath, tiplistId: string, jumpId: Array<string>): QuestionChainIds {
    const questions: Array<string> = paths.map((path) => {
      return path.question;
    });
    const answers: Array<string> = paths.map((path) => {
      return path.answer;
    });

    return new QuestionChainIds({
      questions: questions,
      answers: answers,
      tiplist: tiplistId,
      jump: jumpId,
    });
  }

  private getJumpAnswers(path: string, allNodes: Node.Set, allEdges: Edge.Set): Array<string> {
    const a = path.split('_');
    const res = [];
    if (a.length == 1) {
      return res;
    }
    let i = 1;
    let from = a[0];
    while ( i < a.length) {
      const to = from + '_' + a[i];
      const match = allEdges.get().filter((edge: Edge.Edge) => {
        return edge.from === from && edge.to === to;
      });
      if (match.length == 1) {
        res.push(match[0].answerValue);
      }
      else {
        console.error("Failed to get jump path for "+path);
        res.push('???');
      }
      from = to;
      i++;
    }
    return res;
  }

  private getEdgesForNode(node: Node.Node, allEdges: Edge.Set): Array<Edge.Edge> {
    return allEdges.get().filter((edge: Edge.Edge) => {
      return edge.from === node.id;
    });
  }

  private mapQuestionTypesToClass(chain: QuestionChain): QuestionChain {
    chain.questions = chain.questions.map((question: Question) => {
      return this.mapQuestionTypeToClass(question);
    });

    return chain;
  }

  private mapQuestionTypeToClass(question: Question): Question {
    question.type = this.questionTypeFactoryService
      .createQuestionType(question.type.name, question.type);

    return question;
  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/dynamic-tips/tree-view/services/tree-view.service.ts